/**
 * Scaling Up People — A/B/C player assessments (Topgrading).
 *
 * SENSITIVE: super_admin only. Every change goes through a confirmation modal
 * — A/B/C is an emotionally loaded categorization and we want explicit intent.
 *
 * "AI Feedback Draft" button calls /ai/people-feedback to generate a structured
 * suggestion based on objective system data. The output is *advisory* — the
 * human still makes the final tier call.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, Plus, Lock, AlertTriangle } from "lucide-react";

type Tier = "A" | "B" | "C";

interface Holding {
  id: string;
  name: string;
}
interface Company {
  id: string;
  name: string;
}

interface Person {
  id: string;
  userId: string;
  period: string;
  tier: Tier;
  notes: string | null;
  assessedByUserId: string;
  assessedAt: string;
}

interface AiFeedbackDraft {
  userId: string;
  period: string;
  suggestedTier: Tier;
  confidence: "low" | "medium" | "high";
  evidenceFor: ReadonlyArray<string>;
  evidenceAgainst: ReadonlyArray<string>;
  suggestedGrowthActions: ReadonlyArray<string>;
  talkingPoints: ReadonlyArray<string>;
}

const TIER_CLASS: Record<Tier, string> = {
  A: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  B: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  C: "bg-red-500/15 text-red-300 border-red-500/30",
};

const TIER_DESCRIPTION: Record<Tier, string> = {
  A: "A-player: dowozi >100% celów, samodzielnie usuwa blokery, magnetyzuje innych A-playerów.",
  B: "B-player: dowozi większość celów, czasem potrzebuje wsparcia, ma ścieżkę do A.",
  C: "C-player: trwale nie dowozi lub blokuje innych. Plan naprawczy lub ekstrakcja.",
};

function defaultPeriod(): string {
  // First day of current quarter (YYYY-MM-DD)
  const now = new Date();
  const month = Math.floor(now.getMonth() / 3) * 3;
  return new Date(Date.UTC(now.getFullYear(), month, 1)).toISOString().slice(0, 10);
}

function ScalingUpPeopleInner() {
  const qc = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>(defaultPeriod());
  const [showAdd, setShowAdd] = useState(false);

  const holdingsQuery = useQuery<{ items: Holding[] }>({ queryKey: ["/api/scaling-up/holdings"] });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });
  const peopleKey: readonly string[] = [
    "/api/scaling-up/companies",
    companyId ?? "",
    `people?period=${period}`,
  ];
  const peopleQuery = useQuery<{ items: Person[] }>({
    queryKey: peopleKey,
    enabled: companyId !== null,
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const people = peopleQuery.data?.items ?? [];

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <header className="mb-6">
        <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-widest mb-1">
          <Lock className="w-3 h-3" /> Scaling Up · Sensitive
        </div>
        <h1 className="text-2xl lg:text-3xl font-extrabold">People — Topgrading (A/B/C)</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Wszystkie zmiany są audit-logged. Każdy A-player magnetyzuje kolejnych A-playerów.
          C-playery wymagają planu naprawczego lub ekstrakcji — to nie jest opcja "zostaw na potem".
        </p>
      </header>

      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId(null);
            }}
          >
            <option value="">Holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(e.target.value || null)}
            disabled={!holdingId}
          >
            <option value="">Spółka…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
          <Button size="sm" onClick={() => setShowAdd((v) => !v)} disabled={!companyId}>
            <Plus className="w-4 h-4 mr-2" /> Nowy assessment
          </Button>
        </CardContent>
      </Card>

      {showAdd && companyId && (
        <PersonAssessmentForm
          companyId={companyId}
          holdingId={holdingId ?? ""}
          defaultPeriod={period}
          onSaved={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: peopleKey });
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {peopleQuery.isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      )}

      {people.length === 0 && !peopleQuery.isLoading && companyId && (
        <div className="text-white/40 text-sm">Brak ocen na ten kwartał.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(["A", "B", "C"] as Tier[]).map((tier) => (
          <Card key={tier} className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Badge className={`border ${TIER_CLASS[tier]} text-base font-bold`}>
                  {tier}-Player
                </Badge>
                <span className="text-white/40 text-xs">
                  {people.filter((p) => p.tier === tier).length}
                </span>
              </div>
              <div className="text-[11px] text-white/40 mb-3 italic">{TIER_DESCRIPTION[tier]}</div>
              <ul className="space-y-2">
                {people
                  .filter((p) => p.tier === tier)
                  .map((p) => (
                    <li key={p.id} className="border border-white/10 rounded p-2 text-sm">
                      <div className="font-mono text-xs">{p.userId.slice(0, 8)}…</div>
                      {p.notes && <div className="text-xs text-white/60 mt-1">{p.notes}</div>}
                      <div className="text-[10px] text-white/40 mt-1">
                        {new Date(p.assessedAt).toLocaleDateString()}
                      </div>
                    </li>
                  ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function PersonAssessmentForm({
  companyId,
  holdingId,
  defaultPeriod,
  onSaved,
  onCancel,
}: {
  companyId: string;
  holdingId: string;
  defaultPeriod: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [userId, setUserId] = useState("");
  const [period, setPeriod] = useState(defaultPeriod);
  const [tier, setTier] = useState<Tier>("B");
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState<AiFeedbackDraft | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const aiMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("userId required");
      return apiRequest("POST", "/api/scaling-up/ai/people-feedback", {
        userId,
        period,
        holdingId,
        companyId,
      });
    },
    onSuccess: (data: AiFeedbackDraft) => {
      setDraft(data);
      setTier(data.suggestedTier);
      setNotes(
        [
          `(AI confidence: ${data.confidence})`,
          "Evidence FOR:",
          ...data.evidenceFor.map((e) => `  - ${e}`),
          "Evidence AGAINST:",
          ...data.evidenceAgainst.map((e) => `  - ${e}`),
          "Growth actions:",
          ...data.suggestedGrowthActions.map((e) => `  - ${e}`),
        ].join("\n")
      );
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/scaling-up/companies/${companyId}/people`, {
        userId,
        period,
        tier,
        notes: notes || null,
      }),
    onSuccess: onSaved,
  });

  return (
    <Card className="mb-4 border-yellow-500/30 bg-yellow-500/[0.04]">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-yellow-300 text-xs">
          <AlertTriangle className="w-4 h-4" /> Wrażliwa operacja — wymaga potwierdzenia przed
          zapisem.
        </div>
        <input
          className="w-full bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm font-mono"
          placeholder="user UUID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <div className="flex gap-2">
          <input
            type="date"
            className="bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
          <select
            className="bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm"
            value={tier}
            onChange={(e) => setTier(e.target.value as Tier)}
          >
            <option value="A">A-Player</option>
            <option value="B">B-Player</option>
            <option value="C">C-Player</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => aiMutation.mutate()}
            disabled={!userId || aiMutation.isPending}
          >
            {aiMutation.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3 mr-1" />
            )}
            AI Draft
          </Button>
        </div>
        <textarea
          className="w-full bg-black/30 border border-white/10 rounded p-3 text-xs h-40 font-mono"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notatki — observable, oparte na danych, bez ataków personalnych."
        />
        {draft && (
          <div className="text-xs text-white/60 border border-white/10 rounded p-2 bg-black/20">
            <div>
              AI sugeruje: <strong>{draft.suggestedTier}</strong> (confidence: {draft.confidence})
            </div>
            {draft.talkingPoints.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer">Talking points 1:1</summary>
                <ul className="list-disc list-inside mt-1">
                  {draft.talkingPoints.map((tp, i) => (
                    <li key={i}>{tp}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={!userId}>
            Zapisz ocenę…
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            Anuluj
          </Button>
        </div>

        {confirmOpen && (
          <ConfirmModal
            tier={tier}
            userId={userId}
            onConfirm={() => {
              setConfirmOpen(false);
              saveMutation.mutate();
            }}
            onCancel={() => setConfirmOpen(false)}
            saving={saveMutation.isPending}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ConfirmModal({
  tier,
  userId,
  onConfirm,
  onCancel,
  saving,
}: {
  tier: Tier;
  userId: string;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onCancel}
    >
      <div
        className="max-w-md w-full bg-[#0a0c12] border border-white/10 rounded-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-5 h-5 text-yellow-300" />
          <div className="font-semibold">Potwierdź ocenę</div>
        </div>
        <div className="text-sm text-white/70 mb-4">
          Zapiszesz ocenę <Badge className={`border mx-1 ${TIER_CLASS[tier]}`}>{tier}-Player</Badge>
          dla użytkownika <span className="font-mono text-xs">{userId.slice(0, 12)}…</span>.
        </div>
        <div className="text-xs text-white/50 mb-4 italic">{TIER_DESCRIPTION[tier]}</div>
        <div className="text-xs text-white/40 mb-4">
          Zapis trafi do audit logu (kto, kiedy, jaka tier, before→after).
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>
            Anuluj
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={saving}>
            {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Potwierdzam zapis
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ScalingUpPeoplePage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpPeopleInner />
    </AdminGuard>
  );
}
